<template>
  <main class="login-page">
    <!-- Globe band with white cards on it, like the Manager's start page. The login is the page's main job,
         so it is the white card; the marketing text only sits on the picture next to it. -->
    <section class="lp-hero">
      <header class="lp-header">
        <img src="@/assets/logo_dm_sidebar.png" class="lp-logo" alt="A3M Destination Manager" />
      </header>

      <div class="lp-hero-grid">
        <v-card class="lp-login" elevation="8" rounded="lg">
          <h1 class="lp-h1">{{ mt('ui.login.WelcomeBack') }}</h1>
          <p class="lp-subtitle">{{ mt('ui.login.PleaseLogin') }}</p>

          <v-form ref="form" @submit.prevent="submitLogin">
            <v-text-field
              :label="mt('ui.login.Username')"
              :autofocus="true"
              variant="underlined"
              v-model="username"
              autocomplete="username"
              :rules="[rules.required]" />
            <v-text-field
              :label="mt('ui.login.Password')"
              variant="underlined"
              class="password-field"
              v-model="password"
              autocomplete="current-password"
              :type="showPassword ? 'text' : 'password'"
              :append-inner-icon="showPassword ? 'visibility' : 'visibility_off'"
              :rules="[rules.required]"
              @click:append-inner="showPassword = !showPassword" />

            <p class="login-failed" v-if="showLoginError">{{ mt('ui.login.LoginFailedMessage') }}</p>

            <!-- Always enabled (a pale disabled button looks broken); the fields are validated on submit. -->
            <v-btn type="submit" color="primary" height="52" block>{{ mt('ui.login.DoLogin') }}</v-btn>
            <div class="lp-forgot">
              <a :href="`/auth/forgot-password?lang=${language}`" class="forgot-password-btn">{{ mt('ui.login.ForgotPassword') }}</a>
            </div>
          </v-form>

          <div class="lp-language">
            <LanguageSelector />
          </div>

          <p class="lp-cookie" v-html="mt('ui.login.CookieNotice')" />
        </v-card>

        <!-- Hero right: marketing text on the band. The band's background image is a placeholder and may change. -->
        <div class="lp-visual">
          <h2 class="lp-visual-title">{{ mt('ui.login.VisualHeadline') }}</h2>
          <p class="lp-visual-text">{{ mt('ui.login.VisualSubtitle') }}</p>
          <!-- Price plus one action: the free trial, the natural first step for a new visitor.
               Ordering stays in its card further down. -->
          <div class="lp-offer">
            <p class="lp-price">
              <span>{{ mt('ui.login.PricePrefix') }}</span>
              <strong>{{ mt('ui.login.PriceAmount') }}</strong>
              <span>{{ mt('ui.login.PricePerMonth') }}</span>
            </p>
            <v-btn class="lp-offer-btn" color="#fff" height="48" :href="mt('ui.login.TrialUrl')" target="_blank" rel="noopener">{{ mt('ui.login.TrialButton') }}</v-btn>
            <!-- Secondary "tell me more" path right under the trial; the same link also closes the benefits section. -->
            <a class="lp-learn-more lp-learn-more--on-dark" :href="mt('ui.login.LearnMoreUrl')" target="_blank" rel="noopener">
              {{ mt('ui.login.LearnMore') }} <v-icon size="18">arrow_forward</v-icon>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Lower half: first what you get (benefits), then the two ways in (trial / order) as the closing step. -->
    <section class="lp-benefits">
      <div class="lp-section-head">
        <h2 class="lp-section-title">{{ mt('ui.login.BenefitsTitle') }}</h2>
        <p class="lp-section-intro">{{ mt('ui.login.BenefitsIntro') }}</p>
      </div>
      <ul class="lp-benefit-grid">
        <li class="lp-benefit" v-for="n in 6" :key="n">
          <span class="lp-benefit-icon"><v-icon>{{ benefitIcons[n - 1] }}</v-icon></span>
          <p>{{ mt('ui.login.Benefit' + n) }}</p>
        </li>
      </ul>
      <a class="lp-learn-more" :href="mt('ui.login.LearnMoreUrl')" target="_blank" rel="noopener">
        {{ mt('ui.login.LearnMore') }} <v-icon size="18">arrow_forward</v-icon>
      </a>
    </section>

    <section class="lp-cta">
      <div class="lp-section-head">
        <h2 class="lp-section-title">{{ mt('ui.login.NoAccessTitle') }}</h2>
      </div>
      <div class="lp-cta-grid">
        <v-card class="lp-card" elevation="2" rounded="lg">
          <div class="lp-card-icon"><v-icon>favorite_border</v-icon></div>
          <h3 class="lp-card-title">{{ mt('ui.login.TrialTitle') }}</h3>
          <p class="lp-card-text">{{ mt('ui.login.TrialText') }}</p>
          <v-btn class="lp-card-btn" color="primary" variant="outlined" height="44" :href="mt('ui.login.TrialUrl')" target="_blank" rel="noopener">{{ mt('ui.login.TrialButton') }}</v-btn>
        </v-card>

        <v-card class="lp-card" elevation="2" rounded="lg">
          <div class="lp-card-icon"><v-icon>shopping_cart</v-icon></div>
          <h3 class="lp-card-title">{{ mt('ui.login.OrderTitle') }}</h3>
          <p class="lp-card-text">{{ mt('ui.login.OrderText') }}</p>
          <v-btn class="lp-card-btn" color="primary" height="44" :href="mt('ui.login.OrderUrl')" target="_blank" rel="noopener">{{ mt('ui.login.OrderButton') }}</v-btn>
        </v-card>
      </div>
    </section>

    <footer class="lp-footer">
      <span>{{ mt('ui.login.FooterCopyright') }}</span>
      <a :href="mt('ui.login.PrivacyUrl')" target="_blank" rel="noopener">{{ mt('ui.login.Privacy') }}</a>
      <a :href="mt('ui.login.ImprintUrl')" target="_blank" rel="noopener">{{ mt('ui.login.Imprint') }}</a>
      <a :href="mt('ui.login.ContactUrl')" target="_blank" rel="noopener">{{ mt('ui.login.Contact') }}</a>
    </footer>
  </main>
</template>

<script>
import { mapActions, mapState } from "vuex";
import LanguageSelector from '@/components/ui/LanguageSelector'

export default {
  name: "AppLogin",
  components: {
    LanguageSelector
  },
  data() {
    return {
      username: "",
      password: "",
      showLoginError: false,
      showPassword: false,
      rules: {
        required: value => !!value || this.mt('ui.field.RequiredField')
      },
      // Material icon ligatures for the six benefit tiles (placeholder choices).
      benefitIcons: ['public', 'directions_boat', 'notifications', 'description', 'account_balance_wallet', 'warning'],
    }
  },
  methods: {
    ...mapActions({
      login: 'authStore/login'
    }),

    // Translate a login-page string with English (not the global German) fallback: use the
    // active language where a translation exists, otherwise fall back to English. Task 26362.
    mt(key) {
      return this.$te(key) ? this.$t(key) : this.$t(key, {}, { locale: 'en' });
    },

    submitLogin: async function() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }
      this.login({
        username: this.username,
        password: this.password
      }).then(() => {
        this.showLoginError = false

        // Only honor internal paths ("/..." but not protocol-relative "//...") to avoid open
        // redirects. Legacy values (route names from the old format) fall back to the start page.
        const afterlogin = this.$route.query.afterlogin;
        if (typeof afterlogin === 'string' && afterlogin.startsWith('/') && !afterlogin.startsWith('//')) {
          this.$router.push(afterlogin);
        } else {
          this.$router.push({ name: "index" });
        }
      }).catch((e) => {
        this.showLoginError = true;
      })
    }
  },
  computed: {
    ...mapState({
      language: state => state.uiStore.language
    })
  }
};
</script>

<style scoped>
  /* Colours and font follow the Manager itself: Roboto, brand navy, sidebar navy and the Manager's
     action blue as the accent (the original cognac was dropped, ticket #27578). */
  .login-page {
    --blue: #003b71;
    --navy: #032545;
    --accent: #1867c0;
    --light: #f7f9fc;
    --text: #1f2933;
    /* Content is capped at this width on wide screens; section backgrounds stay full-bleed. */
    --content-max: 1280px;
    --gutter: max(40px, 50% - var(--content-max) / 2);
    /* Width of the form fields; the login card is sized to hug them (plus its padding). */
    --form-width: 440px;
    --login-card-width: calc(var(--form-width) + 96px);

    font-family: Roboto, sans-serif;
    color: var(--text);
    background: #fff;
    line-height: 1.45;
  }

  .login-page a { color: var(--blue); text-decoration: none; }
  .login-page a:hover { text-decoration: underline; }

  /* Full-width globe band, like the top of the Manager's start page. */
  .lp-hero {
    /* Placeholder image - may change (see ticket). */
    /* Navy scrim over the busy picture so the marketing text stays readable; the globe still shows on the right. */
    background:
      linear-gradient(90deg, rgba(3, 37, 69, .92) 0%, rgba(3, 37, 69, .82) 60%, rgba(3, 37, 69, .25) 100%),
      var(--navy) url("../assets/prop-background.jpg") center/cover no-repeat;
    padding: 24px var(--gutter) 48px;
  }

  .lp-header { padding-bottom: 16px; }
  /* Close to the Manager sidebar's 90px logo; slightly smaller so the login card stays above the fold. */
  .lp-logo { height: 80px; width: auto; display: block; }

  .lp-hero-grid {
    display: grid;
    grid-template-columns: var(--login-card-width) 1fr;
    gap: 64px;
    /* Top-aligned: the marketing headline starts on the same line as the card's heading. */
    align-items: start;
  }

  .lp-login { padding: 40px 48px 28px; }
  .lp-login form { max-width: var(--form-width); }

  .lp-h1 { color: var(--blue); font-size: 30px; font-weight: 500; margin: 0 0 4px; }
  .lp-subtitle { margin: 0 0 24px; color: #3f4650; font-size: 17px; }

  .login-failed { color: #b00020; margin-bottom: 1rem; }
  .password-field { margin-bottom: 1rem; }

  .lp-forgot { margin: 14px 0 8px; text-align: right; }
  .login-page .forgot-password-btn { color: var(--blue); font-size: 15px; }

  .lp-language { margin: 1.25rem 0 1rem; max-width: var(--form-width); }
  .lp-cookie { color: #5f6874; font-size: 13px; max-width: var(--form-width); margin: 0; }
  /* The notice is v-html, so its link is outside the scoped styles. */
  .lp-cookie :deep(a) { color: var(--blue); }

  .lp-visual { color: #fff; padding-top: 40px; }

  .lp-visual-title {
    color: #fff;
    font-size: 40px;
    font-weight: 500;
    line-height: 1.1;
    margin: 0 0 16px;
    max-width: 10em;
  }

  .lp-visual-text { color: #d6e4f3; font-size: 19px; max-width: 400px; margin: 0; }

  /* Offer: price as plain white text with the white button directly under it, both on the text's left edge -
     the band's only action, below the login in weight (the login keeps the blue button). */
  .lp-offer {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    margin-top: 32px;
  }

  .lp-price { display: flex; align-items: baseline; gap: 8px; margin: 0; color: #fff; }
  .lp-price span { font-size: 16px; color: #d6e4f3; }
  .lp-price strong { font-size: 32px; font-weight: 500; line-height: 1.1; }

  .lp-offer-btn { color: var(--blue) !important; font-weight: 500; min-width: 240px; }

  /* Lower half: two sections on one rhythm - 64px vertical padding, left-aligned heading,
     content on the same left edge as the login card. White, then light grey. */
  .lp-benefits, .lp-cta { padding: 64px var(--gutter); }
  .lp-benefits { background: #fff; }
  .lp-cta { background: var(--light); border-top: 1px solid #e6ebf2; }

  .lp-section-head { margin-bottom: 32px; }
  .lp-section-title { color: var(--blue); font-size: 28px; font-weight: 500; margin: 0; }
  .lp-section-intro { color: #515b66; font-size: 17px; margin: 8px 0 0; }

  /* Benefits: 3 x 2 grid, each item an icon tile plus text, separated by hairlines - structure without boxes. */
  .lp-benefit-grid {
    list-style: none;
    padding: 0;
    margin: 0 0 32px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 40px;
  }

  .lp-benefit {
    display: grid;
    grid-template-columns: 48px 1fr;
    gap: 16px;
    align-items: center;
    padding: 20px 0;
    border-top: 1px solid #e6ebf2;
  }

  .lp-benefit-icon {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    /* A touch of colour: the Manager's action blue on a light tint of it. */
    background: #e3eefb;
    color: var(--accent);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .lp-benefit-icon :deep(.v-icon) { font-size: 26px; }
  .lp-benefit p { margin: 0; font-size: 16px; color: var(--text); }

  .login-page .lp-learn-more {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--accent);
    font-weight: 500;
    font-size: 16px;
  }

  .login-page .lp-learn-more--on-dark { color: #fff; }

  /* Offers: two equal white cards, the same build; trial outlined, order filled. */
  .lp-cta-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  /* Icon left of the title, text and button indented under the title - the same icon-left pattern as the
     benefits above. Rows: title / text (stretches, so both buttons line up) / button. */
  .lp-card {
    padding: 32px;
    display: grid;
    grid-template-columns: 48px 1fr;
    grid-template-rows: auto 1fr auto;
    column-gap: 16px;
  }
  .lp-card-title, .lp-card-text, .lp-card-btn { grid-column: 2; }

  .lp-card-icon {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    /* A touch of colour: the Manager's action blue on a light tint of it. */
    background: #e3eefb;
    color: var(--accent);
    display: flex;
    align-items: center;
    justify-content: center;
    grid-column: 1;
    grid-row: 1;
  }
  .lp-card-icon :deep(.v-icon) { font-size: 26px; }

  .lp-card-title { align-self: center; color: var(--blue); font-size: 22px; font-weight: 500; margin: 0; }
  .lp-card-text { margin: 8px 0 24px; color: #3f4650; font-size: 16px; }
  .lp-card-btn { justify-self: start; min-width: 220px; }

  .lp-footer {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px 24px;
    background: var(--navy);
    color: #fff;
    padding: 18px;
    font-size: 14px;
  }

  .login-page .lp-footer a { color: #fff; }

  /* Portrait tablets and below: one column, login card first. */
  @media (max-width: 960px) {
    .lp-hero-grid, .lp-cta-grid { grid-template-columns: 1fr; }
    .lp-hero-grid { gap: 32px; }
    .lp-benefit-grid { grid-template-columns: repeat(2, 1fr); }
    .lp-hero { padding: 24px 24px 40px; }
    .lp-login, .lp-visual { width: 100%; max-width: var(--login-card-width); margin: 0 auto; }
    .lp-visual { padding-top: 0; }
    .lp-visual-title { font-size: 32px; }
    .lp-cta, .lp-benefits { padding: 48px 24px; }
  }

  /* Phones: full-width login, compact marketing blocks. */
  @media (max-width: 600px) {
    .lp-header { padding-bottom: 12px; }
    .lp-logo { height: 56px; }
    .lp-hero { padding: 16px 12px 32px; }
    .lp-hero-grid { gap: 24px; }
    .lp-login { padding: 28px 20px 20px; }
    .lp-h1 { font-size: 26px; }
    .lp-visual { padding: 0 8px; }
    .lp-visual-title { font-size: 26px; }
    .lp-visual-text { font-size: 16px; }
    .lp-offer { margin-top: 24px; }
    .lp-price strong { font-size: 26px; }
    .lp-offer-btn { width: 100%; }
    .lp-benefit-grid { grid-template-columns: 1fr; }
    .lp-benefit { grid-template-columns: 40px 1fr; padding: 14px 0; }
    .lp-benefit-icon { width: 40px; height: 40px; }
    .lp-card { padding: 24px 20px; grid-template-columns: 40px 1fr; column-gap: 14px; }
    .lp-card-icon { width: 40px; height: 40px; }
    /* On phones the button takes the card's full width, under the icon too. */
    .lp-card-btn { grid-column: 1 / -1; width: 100%; }
    .lp-section-title { font-size: 22px; }
    .lp-cta, .lp-benefits { padding: 40px 16px; }
  }
</style>
